import { createContext, useContext, useEffect, useMemo, useState } from "react";
import AXIOSCONFIGMETHOD from "../../../Axios/axiosConfigMethod";
import { webServiceUrl } from "../../api_utils/webserviceUrl";
import {
  setLocalStorageOrganization,
  setLocalStorageProperty,
} from "../../../utils/localStorageItem";
import { useApi } from "../../../hooks/useAPI";
import { getBearerToken } from "../../../utils/authToken";

type AuthContextType = {
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  token: string | null;
  setToken: React.Dispatch<React.SetStateAction<string | null>>;
  loading: boolean;
  selectedOrganization: any;
  fetchOrganizationData: () => void;
  orgLoading: boolean;
  setOrgLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedOrganization: React.Dispatch<React.SetStateAction<any | null>>;
  selectedProperty: any;
  setSelectedProperty: React.Dispatch<React.SetStateAction<any | null>>;
  organizationList: any[];
  setOrganizationList: React.Dispatch<React.SetStateAction<any | null>>;
  organizationListOption: any[];
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  setIsAuthenticated: () => {},
  token: null,
  setToken: () => {},
  loading: true,
  fetchOrganizationData: () => {},
  setOrgLoading: () => {},
  orgLoading: true,
  selectedOrganization: null,
  setSelectedOrganization: () => {},
  selectedProperty: null,
  setSelectedProperty: () => {},
  organizationList: [],
  setOrganizationList: () => {},
  organizationListOption: [],
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("AuthContext must be inside it's context");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [token, setToken] = useState<any>();
  const [organizationList, setOrganizationList] = useState<any[]>([]);
  const [orgLoading, setOrgLoading] = useState(false);

  // const [propertyList, setPropertyList] = useState<any[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<any>(() =>
    localStorage.getItem("selected-organization")
      ? JSON.parse(localStorage.getItem("selected-organization") || "")
      : null,
  );
  const [selectedProperty, setSelectedProperty] = useState<any>(() =>
    localStorage.getItem("selected-property")
      ? JSON.parse(localStorage.getItem("selected-property") || "")
      : null,
  );

  const isErrorPage =
    typeof window !== "undefined" &&
    window.location.pathname.includes("error");

  //   --- keep updating state as localstorage "key" updates
  useEffect(() => {
    const storageHandlers: Record<
      string,
      React.Dispatch<React.SetStateAction<any | null>>
    > = {
      "selected-organization": setSelectedOrganization,
      "selected-property": setSelectedProperty,
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (!event.key || !(event.key in storageHandlers)) return;
      try {
        const parsedValue = JSON.parse(event.newValue || "");
        storageHandlers[event.key](parsedValue);
      } catch (error) {
        console.error(`Error parsing storage value `, error);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function (key, value) {
      originalSetItem.call(this, key, value);
      if (key in storageHandlers) {
        window.dispatchEvent(
          new StorageEvent("storage", { key, newValue: value }),
        );
      }
    };

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);
  //   ---------------- End -----------------------

  useEffect(() => {
    const storedToken = getBearerToken();
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
    } else {
      setToken(null);
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, []);

  // --------------------------------------------------

  // console.log(window.location.href?.includes("error"));
  const {
    data: organiztionData,
    loading: Loading,
    callApi: fetchOrg,
  } = useApi<any>(webServiceUrl.GET_ALL_ORGANIZATIONS, {
    method: "POST",
    autoCall: false,
    dependencies: [],
  });

  const fetchOrganizationData = async () => {
    setOrgLoading(true);
    try {
      const res = await fetchOrg({});
      // console.log(res);
      if (res?.success) {
        const final = res?.data;
        setOrganizationList(final?.length > 0 ? final : []);
      }
    } catch (error) {
      console.error("Error: ", error);
    } finally {
      setOrgLoading(false);
    }
  };

  useEffect(() => {
    if (!isErrorPage && isAuthenticated) {
      fetchOrganizationData();
    }
  }, [isAuthenticated]);

  const organizationListOption = useMemo<
    {
      value: string;
      label: string;
    }[]
  >(() => {
    const list =
      organizationList?.length > 0
        ? organizationList
            ?.filter(
              (org: any) =>
                org?.organization_name?.trim() &&
                org?.status?.toLowerCase() == "active",
            )
            ?.map((org: any) => ({
              value: org?.organization_id,
              label: org?.organization_name,
            }))
        : [];
    return [...list];
  }, [organizationList]);

  // useEffect(() => {
  //   setLocalStorageOrganization({
  //     organization_id: "",
  //     organization_name: "",
  //   });
  //   setLocalStorageProperty({ property_id: "", property_name: "" });
  // }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        token,
        setToken,
        loading,
        orgLoading,
        setOrgLoading,
        fetchOrganizationData,
        selectedOrganization,
        setSelectedOrganization,
        selectedProperty,
        setSelectedProperty,
        organizationList,
        setOrganizationList,
        organizationListOption,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

// =-=======================

// const fetchOrganizationData = async () => {
//   try {
//     setOrgLoading(true);
//     const resp = await AXIOSCONFIGMETHOD(
//       "post",
//       webServiceUrl.GET_ALL_ORGANIZATIONS,
//     );
//     // console.log(resp?.data)
//     if (resp?.data?.success) {
// const final = resp?.data?.data;
// setOrganizationList(final?.length > 0 && final);
//     }
//   } catch (error) {
//     console.error("Error fetching  data:", error);
//   } finally {
//     setOrgLoading(false);
//   }
// };

//   --- keep updating state as localstorage "key" updates
// useEffect(() => {
//   const storageHandlers = {
//     "selected-organization": setSelectedOrganization,
//     "selected-property": setSelectedProperty,
//   };

//   const handleStorageChange = (event: StorageEvent) => {
//     if (!event.key || !(event.key in storageHandlers)) return;
//     if (
//       event.key === "selected-organization"
//       // || event.key === "selected-property"
//     ) {
//       //   console.log("event --- ", event);
//       const finalValue = JSON.parse(event.newValue || "");
//       setSelectedOrganization(finalValue);
//     }
//   };
//   window.addEventListener("storage", handleStorageChange);
//   const originalSetItem = localStorage.setItem;
//   localStorage.setItem = function (key, value) {
//     //   originalSetItem.apply(this, arguments);
//     originalSetItem.call(this, key, value);
//     if (
//       key === "selected-organization"
//       // || key === "selected-property"
//     ) {
//       window.dispatchEvent(
//         new StorageEvent("storage", { key, newValue: value })
//       );
//     }
//   };
//   return () => {
//     window.removeEventListener("storage", handleStorageChange);
//     localStorage.setItem = originalSetItem;
//   };
// }, []);
